// Telefon- und Brief-Symbol eines Kontakts in der Kontakte-Liste (C02). Dunkel = vorhanden, hell = fehlt. Klick aufs Telefon
// ruft die erste Nummer an, Klick auf den Brief öffnet den E-Mail-Komposer; beim Überfahren zeigt eine kleine Karte ALLE Nummern
// bzw. Adressen des Kontakts, jede mit eigenem Anruf-/Mailknopf und Kopieren (besser als Close, das nur das Symbol zeigt).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { KontaktZeile } from "../api/kontakte";
import { KopierKnopf } from "../components/Bausteine";
import { Icon } from "../components/Icon";
import { telefonText, telefonTyp, useAnrufen } from "../components/Telefonnummer";
import { emailVerfassen } from "../daten/schreiben";
import { useTelefonie } from "../telefonie";

type Karte = "telefon" | "mail" | null;

export function KontaktWege({ k }: { k: KontaktZeile }) {
  const tel = useTelefonie();
  const anrufen = useAnrufen();
  const telefone = k.phones.filter((t) => t.phone);
  const mails = k.emails.filter((m) => m.email);
  const [karte, setKarte] = useState<Karte>(null);
  const [anker, setAnker] = useState<HTMLElement | null>(null);
  const zeit = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(zeit.current), []);

  const zeigen = (art: Exclude<Karte, null>, el: HTMLElement, sofort = false) => {
    window.clearTimeout(zeit.current);
    const auf = () => {
      setAnker(el);
      setKarte(art);
    };
    if (sofort || karte) auf();
    else zeit.current = window.setTimeout(auf, 350);
  };
  const verbergen = () => {
    window.clearTimeout(zeit.current);
    zeit.current = window.setTimeout(() => setKarte(null), 180);
  };
  const halten = () => window.clearTimeout(zeit.current);

  const bereit = tel.status !== "nicht_eingerichtet";
  const erste = telefone[0];
  const telTitel = erste ? (bereit ? `Anrufen: ${telefonText(erste.phone, erste.phone_formatted)}` : `Nummer kopieren: ${telefonText(erste.phone, erste.phone_formatted)}`) : "Keine Telefonnummer";
  const mailTitel = mails[0] ? `E-Mail schreiben: ${mails[0].email}` : "Keine E-Mail-Adresse";
  const namen = { leadName: k.lead_name, kontaktName: k.name };

  const anrufenAn = (nummer: string) => {
    setKarte(null);
    void anrufen(nummer, k.lead_id, k.id, namen);
  };
  const mailAn = (adresse: string) => {
    setKarte(null);
    emailVerfassen({ leadId: k.lead_id, contactId: k.id, an: adresse, leadName: k.lead_name });
  };

  return (
    <span className="kontaktwege">
      <button
        type="button"
        className="kontaktweg"
        disabled={!erste}
        aria-label={telTitel}
        onMouseEnter={(e) => erste && zeigen("telefon", e.currentTarget)}
        onMouseLeave={verbergen}
        onFocus={(e) => erste && e.currentTarget.matches(":focus-visible") && zeigen("telefon", e.currentTarget, true)}
        onBlur={verbergen}
        onClick={(e) => {
          e.stopPropagation();
          if (erste) anrufenAn(erste.phone);
        }}
      >
        <Icon name="telefon" groesse={14} />
      </button>
      {mails[0] ? (
        <a
          className="kontaktweg"
          href={`mailto:${mails[0].email}`}
          aria-label={mailTitel}
          onMouseEnter={(e) => zeigen("mail", e.currentTarget)}
          onMouseLeave={verbergen}
          onFocus={(e) => e.currentTarget.matches(":focus-visible") && zeigen("mail", e.currentTarget, true)}
          onBlur={verbergen}
          onClick={(e) => {
            e.stopPropagation();
            setKarte(null);
            if (emailVerfassen({ leadId: k.lead_id, contactId: k.id, an: mails[0].email, leadName: k.lead_name })) e.preventDefault();
          }}
        >
          <Icon name="mail" groesse={14} />
        </a>
      ) : (
        <button type="button" className="kontaktweg" disabled aria-label={mailTitel} title={mailTitel}>
          <Icon name="mail" groesse={14} />
        </button>
      )}
      {karte && anker ? (
        <WegeKarte anker={anker} onBetreten={halten} onVerlassen={verbergen}>
          {karte === "telefon" ? (
            <>
              {telefone.map((t) => (
                <div key={t.phone} className="kw-karte__zeile">
                  <button type="button" className="kw-karte__wert" title={bereit ? "Anrufen" : "Nummer kopieren"} onClick={() => anrufenAn(t.phone)}>
                    <Icon name="telefon" groesse={13} />
                    <span>{telefonText(t.phone, t.phone_formatted)}</span>
                  </button>
                  {t.type ? <span className="kw-karte__typ">{telefonTyp(t.type)}</span> : null}
                  <KopierKnopf text={t.phone} label="Nummer kopieren" />
                </div>
              ))}
              <div className="kw-karte__hinweis">{bereit ? "Klick auf eine Nummer ruft an" : `${tel.grund ?? "Telefonie nicht bereit."} Klick kopiert die Nummer.`}</div>
            </>
          ) : (
            <>
              {mails.map((m) => (
                <div key={m.email} className="kw-karte__zeile">
                  <button type="button" className="kw-karte__wert" title="E-Mail schreiben" onClick={() => mailAn(m.email)}>
                    <Icon name="mail" groesse={13} />
                    <span>{m.email}</span>
                  </button>
                  {m.is_unsubscribed ? <span className="kw-karte__typ kw-karte__typ--warn">abgemeldet</span> : null}
                  <KopierKnopf text={m.email} label="E-Mail kopieren" />
                </div>
              ))}
              <div className="kw-karte__hinweis">Klick auf eine Adresse öffnet eine neue E-Mail</div>
            </>
          )}
        </WegeKarte>
      ) : null}
    </span>
  );
}

/** Karte unter dem Symbol (Portal, feste Position, bleibt im Fenster, schließt beim Scrollen). */
function WegeKarte({ anker, children, onBetreten, onVerlassen }: { anker: HTMLElement; children: ReactNode; onBetreten: () => void; onVerlassen: () => void }) {
  const karte = useRef<HTMLDivElement>(null);
  const [lage, setLage] = useState<{ left: number; top: number } | null>(null);
  useEffect(() => {
    const r = anker.getBoundingClientRect();
    const breite = karte.current?.offsetWidth ?? 260;
    const hoehe = karte.current?.offsetHeight ?? 60;
    const left = Math.max(8, Math.min(r.left - 8, window.innerWidth - breite - 8));
    const unten = r.bottom + 6 + hoehe <= window.innerHeight - 8;
    setLage({ left, top: unten ? r.bottom + 6 : r.top - hoehe - 6 });
    const weg = () => onVerlassen();
    window.addEventListener("scroll", weg, true);
    return () => window.removeEventListener("scroll", weg, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anker]);
  return createPortal(
    <div
      ref={karte}
      className="kw-karte"
      role="tooltip"
      style={{ left: lage?.left ?? -10000, top: lage?.top ?? 0, opacity: lage ? 1 : 0 }}
      onMouseEnter={onBetreten}
      onMouseLeave={onVerlassen}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}
